import { registerLocaleData } from '@angular/common';
import localeEnglish from '@angular/common/locales/en';
import localeFrench from '@angular/common/locales/fr';
import localeDutch from '@angular/common/locales/nl';
import { Injectable, inject } from '@angular/core';
import { CompiereLanguage } from '@compiere-ws/models/compiere-language-json';
import { CompiereLoginService } from '@compiere-ws/services/compiere-login/compiere-login.service';
import { ResourceAccessService } from '@compiere-ws/services/resource-access/resource-access.service';
import { AvatarService } from '@iupics-components/standard/user/avatar-ui/avatar-service/avatar.service';
import { AppConfig } from '@iupics-config/app.config.service';
import { RoleUI, UserAccount } from '@iupics-manager/models/user-account';
import { DateUtils } from '@iupics-util/tools/date.utils';
import { environment } from 'environments/environment';
import jwt_decode from 'jwt-decode';
import { KeycloakService } from 'keycloak-angular';
import { KeycloakProfile } from 'keycloak-js';
import { Observable, defer, from, iif, of, switchMap, zip } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
@Injectable({
  providedIn: 'root',
})
export class SecurityManagerService {
  readonly #loginService = inject(CompiereLoginService);
  readonly #avatarService = inject(AvatarService);
  readonly #appConfig = inject(AppConfig);
  readonly #resourceAccessService = inject(ResourceAccessService);
  protected readonly keycloak = inject(KeycloakService);

  private iupicsDefaultLanguage: CompiereLanguage;
  private iupicsUserAccount: UserAccount;
  private iupicsUserContext: any;

  getAccessToken(): Observable<string> {
    return from(this.keycloak.getToken());
  }

  initContext(profile: KeycloakProfile): Observable<UserAccount> {
    return <Observable<UserAccount>>this.getAccessToken().pipe(
      switchMap((access_token) => {
        const decodedToken = jwt_decode(access_token);
        if (!this.iupicsDefaultLanguage) {
          this.iupicsDefaultLanguage = decodedToken['session']?.['webSession'] ?? environment.default_language;
        }

        return from(this.#appConfig.loadConfig(access_token)).pipe(
          tap(() => this.#loginService.loadUrl()),
          switchMap(() =>
            iif(
              () =>
                this.#appConfig.isModuleEnable('apiz-monowindow') &&
                !!this.#appConfig.getModuleConfig('apiz-monowindow'),
              defer(() => {
                let type: 'P' | 'X' | 'W';

                switch (this.#appConfig.getModuleConfig('apiz-monowindow').menuType) {
                  case 'PROCESS':
                    type = 'P';
                    break;
                  case 'FORM':
                    type = 'X';
                    break;
                  default:
                    // WINDOW
                    type = 'W';
                    break;
                }

                return this.#resourceAccessService.getAccessRoles(
                  type,
                  this.#appConfig.getModuleConfig('apiz-monowindow').menuId
                );
              }),
              of([])
            )
          ),
          switchMap((roles) => {
            let obs: Observable<unknown>;

            if (
              this.#appConfig.isModuleEnable('apiz-monowindow') &&
              this.#appConfig.getModuleConfig('apiz-monowindow').roleId > -1
            ) {
              obs = this.#loginService.getRoleCtx(this.#appConfig.getModuleConfig('apiz-monowindow').roleId);
            } else if (roles.length == 1) {
              obs = this.#loginService.getRoleCtx(roles[0]);
            } else obs = this.#loginService.getCtx();

            return obs.pipe(map((result) => ({ ctxJSON: result, roles })));
          }),
          switchMap(({ ctxJSON, roles }) => {
            const currentAD_Org_ID = ctxJSON['#AD_Org_ID'];

            return this.#loginService.getLoginInfo(access_token).pipe(
              map((loginInfo) => {
                if (loginInfo['org_id'] !== null && loginInfo['org_id'] !== undefined) {
                  ctxJSON['#AD_Org_ID'] = parseInt(loginInfo['org_id'], 10);
                }

                loginInfo.roles = loginInfo.roles.filter((r) => roles.length == 0 || roles.includes(r.role_id));

                let current_role_id = parseInt(ctxJSON['#AD_Role_ID'], 0);
                let current_role = loginInfo.roles.find((r) => r.role_id === current_role_id);
                if (!current_role) {
                  current_role = loginInfo.roles[0];
                }

                const iupicsUserAccount = {
                  id: parseInt(loginInfo.user_id, 0),
                  fullname: profile.firstName + ' ' + profile.lastName,
                  login: profile.username,
                  session_id: parseInt(loginInfo.session_id, 0),
                  email: profile.email,
                  avatarInfos: this.#avatarService.getAvatarInfos({
                    id: parseInt(loginInfo.user_id, 0),
                    fullname: loginInfo.name,
                  }),
                  default_language: this.iupicsDefaultLanguage,
                  current_role: current_role,
                  ad_client: loginInfo.ad_client_id,
                  ...loginInfo,
                };

                for (const role of iupicsUserAccount.roles as RoleUI[]) {
                  if (parseInt(ctxJSON['#AD_Role_ID'], 0) === role.role_id) {
                    role.isSelected = true;
                    iupicsUserAccount.current_role = role;
                  } else {
                    role.isSelected = false;
                  }
                }

                return iupicsUserAccount;
              }),
              tap((iupicsUserAccount) => {
                this.iupicsUserContext = ctxJSON;
                // date courante local setté
                this.setIupicsUserContextDate(DateUtils.toDate(Date.now()));
                if (currentAD_Org_ID !== ctxJSON['#AD_Org_ID']
                    || currentAD_Org_ID !== decodedToken['session']['ad_Org_ID']) {  //#174940
                  this.updateRemoteCtx();
                }

                this.iupicsUserAccount = <any>iupicsUserAccount;
              })
            );
          })
        );
      }),
      catchError((err) => {
        throw err?.error?.message ?? err?.message ?? err;
      })
    );
  }

  changeRole(newRole: RoleUI): Observable<UserAccount> {
    this.iupicsUserContext['#AD_Role_ID'] = newRole.role_id;
    return this.#loginService.updateCtx(this.iupicsUserContext).pipe(
      map((ctx) => {
        this.iupicsUserContext = ctx;
        this.iupicsUserAccount.current_role = newRole;
        return this.iupicsUserAccount;
      })
    );
  }

  getCtx(): Observable<any> {
    return zip(
      this.#loginService.getCtx(),
      this.getAccessToken().pipe(switchMap((access_token) => this.#loginService.getLoginInfo(access_token)))
    ).pipe(
      map(([ctxJson, loginInfo]) => {
        const currentAD_Org_ID = ctxJson['#AD_Org_ID'];
        if (loginInfo['org_id'] !== null && loginInfo['org_id'] !== undefined) {
          ctxJson['#AD_Org_ID'] = parseInt(loginInfo['org_id'], 10);
        }
        this.iupicsUserContext = ctxJson;
        if (currentAD_Org_ID !== ctxJson['#AD_Org_ID']) {
          this.updateRemoteCtx();
        }
        this.iupicsUserAccount.roles = loginInfo.roles;
        for (const role of this.iupicsUserAccount.roles) {
          if (parseInt(ctxJson['#AD_Role_ID'], 0) === role.role_id) {
            role.isSelected = true;
            this.iupicsUserAccount.current_role = role;
          } else {
            role.isSelected = false;
          }
        }

        return this.iupicsUserAccount;
      })
    );
  }

  getIupicsUserAccount(): UserAccount {
    return this.iupicsUserAccount;
  }

  getIupicsUserContext(): any {
    return this.iupicsUserContext;
  }

  getIupicsDefaultLanguage(): CompiereLanguage {
    return this.iupicsDefaultLanguage;
  }

  setIupicsDefaultLanguage(language: CompiereLanguage) {
    this.iupicsDefaultLanguage = language;
    switch (language.iso_code.replace('_', '-')) {
      case 'fr-FR': {
        registerLocaleData(localeFrench);
        break;
      }
      case 'nl-NL': {
        registerLocaleData(localeDutch);
        break;
      }
      case 'en-US': {
        registerLocaleData(localeEnglish);
        break;
      }
      default: {
        registerLocaleData(localeFrench);
        break;
      }
    }

    DateUtils.setLocale(language.iso_code.replace(/_/g, '-'));
  }

  setIupicsUserContextDate(date: Date) {
    this.iupicsUserContext['#Date'] = date.valueOf();
  }
  setIupicsUserContext(ctx: any) {
    this.iupicsUserContext = ctx;
  }

  updateRemoteCtx(): Observable<any> {
    return new Observable((subscriber) => {
      this.#loginService.updateCtx(this.iupicsUserContext).subscribe({
        next: (ctx) => {
          this.iupicsUserContext = ctx;
          subscriber.next(ctx);
          subscriber.complete();
        },
      });
    });
  }

  getConfig(): AppConfig {
    return this.#appConfig;
  }
}
